import React, {forwardRef, useState} from 'react'
import PropTypes from 'prop-types'
import {makeStyles} from '@mui/styles'
import {Colors, Spacing, Typography} from '../../../assets/styles'
import {lightFontWeight} from '../../../assets/styles/typography'
import moment from 'moment'
import {constructClassString} from '../../../Utils/objectUtil'
import {getTextWidth} from '../../../Utils/StringFormatter'

const DEFAULT_NO_DESCRIPTION_TEXT = 'No description provided'

const DetailItemsList = forwardRef(function DetailItemsList(props, ref) {
  const classes = useStyles()
  const [expandedItems, setExpandedItems] = useState(new Set())
  const [maxWidth, setMaxWidth] = useState(0)

  const toggleExpanded = itemIdx => {
    const newExpandedItems = new Set(expandedItems)
    if (newExpandedItems.has(itemIdx)) {
      newExpandedItems.delete(itemIdx)
    } else {
      newExpandedItems.add(itemIdx)
    }
    setExpandedItems(newExpandedItems)
  }

  const ItemComponent = props.itemComponent

  return (
    <div className={classes.container} ref={ref} style={props.style}>
      {props.items.map((item, itemIdx) => {
        if (ItemComponent) {
          return <ItemComponent key={(item && (item.id || item.label)) || itemIdx} item={item} itemIdx={itemIdx} color={props.color} />
        }
        const isExpanded = expandedItems.has(itemIdx)
        const hasDescription = !!item.description
        const textWidth = getTextWidth(item.description || '')
        const needsTruncation = hasDescription && textWidth > maxWidth
        // -16 is to ensure we have enough room to print the "read more" or "read less" text
        const characterLimit = needsTruncation ? Math.floor((maxWidth * item.description.length) / textWidth) - 16 : item.description.length

        return (
          <div className={classes.item} key={item.label + itemIdx}>
            <div className={classes.value} style={{backgroundColor: props.color || Colors.magentaLight}}>
              {item.value}
              {item.denominator ? <span>/{item.denominator}</span> : ''}
            </div>
            <div className={classes.details}>
              <div>
                <h4 className={classes.label}>
                  {item.label} {item.secondaryLabel && <span className={classes.secondaryLabel}>{item.secondaryLabel}</span>}
                </h4>
              </div>
              <div
                className={constructClassString(classes.description, {empty: !hasDescription})}
                ref={r => {
                  if (r && itemIdx === 0) {
                    setMaxWidth(r.getBoundingClientRect().width)
                  }
                }}
              >
                {!hasDescription ? (
                  <p>{props.noDescriptionText || DEFAULT_NO_DESCRIPTION_TEXT}</p>
                ) : !needsTruncation || isExpanded ? (
                  <p>
                    {item.description}
                    {needsTruncation && (
                      <span className={classes.readMoreButton} onClick={() => toggleExpanded(itemIdx)}>
                        read less
                      </span>
                    )}
                  </p>
                ) : (
                  <p>
                    {item.description.substring(0, characterLimit)}
                    <span className={classes.readMoreButton} onClick={() => toggleExpanded(itemIdx)}>
                      &hellip;read more
                    </span>
                  </p>
                )}
              </div>
              <div className={classes.createdAt}>{moment(item.createdAt).format('M/D/YYYY')}</div>
            </div>
          </div>
        )
      })}
      {props.items.length === 0 && (
        <div style={{padding: Spacing.spaceMedium}}>
          <p>No items to show</p>
        </div>
      )}
    </div>
  )
})

const VALUE_CIRCLE_SIZE = 36

const useStyles = makeStyles(theme => ({
  container: {
    border: `1px solid ${Colors.rxrGreyColor}`,
    backgroundColor: Colors.rxrSidePanelBackgroundCoor,
    borderRadius: 4,
    maxHeight: 400,
    overflowY: 'scroll',
  },

  item: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    padding: Spacing.spaceMedium,
  },

  value: {
    flexShrink: 0,
    textAlign: 'center',
    height: VALUE_CIRCLE_SIZE,
    width: VALUE_CIRCLE_SIZE,
    borderRadius: '50%',
    lineHeight: `${VALUE_CIRCLE_SIZE}px`,
    fontSize: Typography.fontSizeMedium,
    fontWeight: Typography.heavyFontWeight,

    '& span': {
      fontSize: Typography.fontSizeExtraSmall,
      fontWeight: Typography.lightFontWeight,
      color: Colors.blackOverlay50,
    },
  },

  details: {
    // the size of the
    width: `calc(100% - ${VALUE_CIRCLE_SIZE}px)`,
    position: 'relative',
    paddingLeft: Spacing.spaceMedium,
  },

  label: {
    fontWeight: 'bold',
    marginBottom: Spacing.spaceExtraExtraSmall,
  },

  secondaryLabel: {
    color: Colors.rxrMediumGreyColor,
    marginLeft: Spacing.spaceSmall,
    fontWeight: lightFontWeight,
  },

  description: {
    width: '100%',
    '&.empty': {
      color: Colors.rxrMediumGreyColor,
    },
  },

  readMoreButton: {
    color: Colors.rxrTintColor,
    cursor: 'pointer',
    fontWeight: Typography.mediumFontWeight,
    textDecoration: 'underline',
  },

  textChunk: {
    margin: `${Spacing.spaceExtraSmall}px 0`,
    lineHeight: 1.4,
  },

  createdAt: {
    position: 'absolute',
    top: 0,
    right: 0,
  },
}))

DetailItemsList.propTypes = {
  items: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
      value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      description: PropTypes.string,
      createdAt: PropTypes.oneOfType([PropTypes.instanceOf(Date), PropTypes.string]),
      denominator: PropTypes.number,
    }).isRequired,
  ),
  itemComponent: PropTypes.elementType,
  noDescriptionText: PropTypes.string,
  color: PropTypes.string,
  style: PropTypes.object,
}

DetailItemsList.defaultProps = {
  items: [],
}

export default DetailItemsList
